/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/auth/ClerkAuthService.ts
 * DESCRIPTION: Clerk implementation of AuthService interface.
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-04-03
 * ─────────────────────────────────────────────────────────────────────
 */

import { AuthService } from './AuthService';
import { getToken, useUser } from '@clerk/react';

export class ClerkAuthService implements AuthService {
  async getToken(): Promise<string | null> {
    try {
      const token = await getToken();
      return token;
    } catch (error) {
      console.error('Failed to get Clerk token:', error);
      return null;
    }
  }
  
  getUserId(): string | null {
    const { user } = useUser();
    return user?.id || null;
  }
  
  getUserEmail(): string | null {
    const { user } = useUser();
    return user?.emailAddresses[0]?.emailAddress || null;
  }
  
  getUserMetadata(): Record<string, any> | null {
    const { user } = useUser();
    return user?.publicMetadata as Record<string, any> || null;
  }
  
  checkPermission(permission: string): boolean {
    const metadata = this.getUserMetadata();
    if (!metadata) return false;
    
    if (permission === 'admin' && metadata.role === 'admin') {
      return true;
    }
    
    if (permission === 'enterprise' && metadata.license === 'enterprise') {
      return true;
    }
    
    if (permission === 'business' && 
        (metadata.license === 'business' || metadata.license === 'enterprise')) {
      return true;
    }
    
    return false;
  }
  
  async signOut(): Promise<void> {
    console.log('Sign out requested - use Clerk signOut() method');
  }
  
  isAuthenticated(): boolean {
    const { isSignedIn } = useUser();
    return !!isSignedIn;
  }
}
